export interface ExcelExportColumn {
  title: string;
  dataIndex: string;
  render?: (val: any, record: any) => string;
}

export interface ExcelExportOptions {
  title: string;
  subtitle?: string;
  columns: ExcelExportColumn[];
  data: any[];
  fileName?: string;
}

export const exportToExcel = ({
  title,
  subtitle = "Delhi Metro Rail Corporation Ltd.",
  columns,
  data,
  fileName = "DMRC_DAR_Report"
}: ExcelExportOptions) => {
  const currentDate = new Date().toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short"
  });

  const headersHtml = columns
    .map(
      col =>
        `<th style="background-color: #002B49; color: #FFFFFF; font-weight: bold; padding: 10px 14px; border: 1px solid #001f35; font-size: 11pt; text-align: left; vertical-align: middle;">${col.title}</th>`
    )
    .join("");

  const rowsHtml = data
    .map((row, idx) => {
      const bgColor = idx % 2 === 0 ? "#FFFFFF" : "#F8F9FA";
      const cells = columns
        .map(col => {
          let value = row[col.dataIndex];
          if (col.render) {
            const rendered = col.render(value, row);
            if (typeof rendered === "string") {
              value = rendered.replace(/<[^>]*>?/gm, "");
            } else {
              value = String(rendered || "-");
            }
          }
          if (value === undefined || value === null || value === "") {
            value = "-";
          }
          const safeValue = String(value)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");
          return `<td style="background-color: ${bgColor}; padding: 8px 12px; border: 1px solid #D0D7DE; color: #1F2328; font-size: 10pt; vertical-align: middle; mso-number-format:'\\@';">${safeValue}</td>`;
        })
        .join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");

  const excelTemplate = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
        <!--[if gte mso 9]>
        <xml>
          <x:ExcelWorkbook>
            <x:ExcelWorksheets>
              <x:ExcelWorksheet>
                <x:Name>${title.replace(/[\\/?*:[\]]/g, "_").slice(0, 30)}</x:Name>
                <x:WorksheetOptions>
                  <x:DisplayGridlines/>
                </x:WorksheetOptions>
              </x:ExcelWorksheet>
            </x:ExcelWorksheets>
          </x:ExcelWorkbook>
        </xml>
        <![endif]-->
        <style>
          body { font-family: 'Calibri', 'Segoe UI', Arial, sans-serif; }
          table { border-collapse: collapse; width: 100%; }
        </style>
      </head>
      <body>
        <table>
          <!-- BRAND HEADER BLOCK -->
          <tr>
            <td colspan="${columns.length}" style="background-color: #800000; color: #FFFFFF; font-size: 16pt; font-weight: bold; padding: 12px 14px; text-align: left;">
              DELHI METRO RAIL CORPORATION LTD.
            </td>
          </tr>
          <tr>
            <td colspan="${columns.length}" style="background-color: #F8F9FA; color: #555555; font-size: 11pt; font-weight: bold; padding: 6px 14px; border-bottom: 2px solid #800000;">
              ${subtitle} &nbsp;|&nbsp; Report: ${title}
            </td>
          </tr>
          <tr>
            <td colspan="${columns.length}" style="color: #666666; font-size: 9pt; padding: 6px 14px;">
              Generated Date: ${currentDate} &nbsp;&nbsp;|&nbsp;&nbsp; Total Records: ${data.length}
            </td>
          </tr>
          <tr><td colspan="${columns.length}" style="height: 10px;"></td></tr>
          
          <!-- TABLE HEADERS -->
          <thead>
            <tr>${headersHtml}</tr>
          </thead>
          
          <!-- TABLE BODY -->
          <tbody>
            ${rowsHtml.length > 0 ? rowsHtml : `<tr><td colspan="${columns.length}" style="text-align: center; padding: 16px; color: #888888;">No records available</td></tr>`}
          </tbody>
        </table>
      </body>
    </html>
  `;

  const blob = new Blob(["\ufeff" + excelTemplate], {
    type: "application/vnd.ms-excel;charset=utf-8;"
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${fileName}_${new Date().toISOString().slice(0, 10)}.xls`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
